import { AppInfoLoader, IAppInfoType } from '@services/AppInfoLoader';
import { IhmAssociateToolsCoreServiceClient } from '@services/coralservices/serviceClients/IhmAssociateToolsCoreServiceClient';
import { CountMetric } from '@src/metrics/CountMetric';
import _ from 'lodash';

/**
 * WeblabService module can be used to fetch weblab treatment assinged to a store.
 */
interface IWeblabTreatment {
    C: string;
    T1: string;
}

interface IAssociateToolsWebLabIdMapping {
    AT_VOICE_FRAMEWORK: string;
    HELIOS_NOTIFICATION: string;
    SLA_AWARE: string;
}

export class WeblabService {
    private static WeblabTreatment: IWeblabTreatment = {
        C: 'C',
        T1: 'T1',
    };

    private static AssociateToolsWebLabIdMapping: IAssociateToolsWebLabIdMapping = {
        AT_VOICE_FRAMEWORK: 'IHM_APT_218738',
        HELIOS_NOTIFICATION: 'IHM_HELIOS_NOTIFICATION_SOUND_387750',
        SLA_AWARE: 'IHM_SLA_AWARE_REPLEN_FRONTEND_393304',
    };

    private static weblabCache = {};

    /**
     * Vocabulary for the `piloting` telemetry field. `Unresolved` is kept distinct from `None`
     * on purpose: a session we failed to read may well be in a pilot, so folding it into "no
     * pilot" would count it as a control sample and bias a latency comparison toward no effect.
     */
    private static PilotingValue = {
        None: 'NONE',
        Pending: 'PENDING',
        Unresolved: 'UNRESOLVED',
    };

    /**
     * Raw treatment name per weblab resolved in this frame, keyed by featureId.
     *
     * Separate from {@link weblabCache} because that holds promises of the collapsed boolean,
     * while telemetry needs a value readable synchronously at event-emit time. Populated from
     * the same resolution that gates the feature, so the label and the behaviour cannot diverge.
     */
    private static resolvedTreatments: Record<string, string> = {};

    /**
     * @param featureId WeblabID for which treatment assignment is needed.
     * @return boolean indicating if this feature is enabled for this store or not.
     */

    public static isFeatureEnabled = (featureId) => {
        return WeblabService.verifyFeatureIsEnabled(featureId)
            .then((response) => {
                return response;
            })
            .catch(() => {
                return false;
            });
    };

    /**
     * @param featureId WeblabID for which treatment assignment is needed.
     * @return boolean indicating if this feature is enabled for this store or not.
     */
    public static isFeatureEnabledWithCache = (featureId) => {
        if (WeblabService.weblabCache[featureId]) {
            return WeblabService.weblabCache[featureId];
        }
        WeblabService.weblabCache[featureId] = WeblabService.verifyFeatureIsEnabled(featureId)
            .then((response) => {
                return response;
            })
            .catch(() => {
                delete WeblabService.weblabCache[featureId];
                return false;
            });
        return WeblabService.weblabCache[featureId];
    };
    /**
     * Pilot enrolments resolved in this frame, formatted for the `piloting` telemetry field as
     * comma-joined `weblab:<featureId>:<treatment>` entries.
     *
     * Reported at event-emit time rather than captured once at telemetry init, so it does not
     * matter when in a tool's lifecycle a weblab is fetched. Returns `NONE` when the tool has
     * asked about no weblab at all.
     *
     * Deliberately never truncated. This value lands in the event `details` blob, which is
     * bounded by RumHelper's 100 KB per-event limit and Firehose's 1 MB per-record limit — a
     * frame's worth of weblabs is a few hundred bytes against that. (RUM's 256-character limit
     * applies to custom *session attributes*, a different path.) Dropping entries to fit a cap
     * would lose exactly the enrolment being analysed, and lose it silently.
     */
    public static getEnrolments = (): string => {
        const entries = Object.entries(WeblabService.resolvedTreatments).map(([featureId, treatment]) => `weblab:${featureId}:${treatment}`);
        return entries.length ? entries.join(',') : WeblabService.PilotingValue.None;
    };

    private static verifyFeatureIsEnabled = (featureId) => {
        let faultmetric;
        // Only mark PENDING when nothing is recorded yet. The uncached isFeatureEnabled re-enters
        // this path on every call, so recording unconditionally would regress an already-resolved
        // label to PENDING for the whole round trip and mislabel events emitted in between.
        if (!(featureId in WeblabService.resolvedTreatments)) {
            WeblabService.recordTreatment(featureId, WeblabService.PilotingValue.Pending);
        }
        return AppInfoLoader.getAppConfig()
            .then((appConfig) => {
                faultmetric = new CountMetric(appConfig.store, 'Fault', 'IhmFlyingDutchman', 'WeblabService.verifyFeatureIsEnabled');
                return IhmAssociateToolsCoreServiceClient.getInstance()
                    .getTreatmentAssignment(appConfig.store, featureId, appConfig.storeInfo.storeFamily)
                    .then((response) => {
                        faultmetric.count(0);
                        const assignmentTreatment = response.body.treatmentAssignment;
                        WeblabService.recordTreatment(featureId, assignmentTreatment.treatmentName ?? WeblabService.PilotingValue.Unresolved);
                        return _.isEqual(WeblabService.WeblabTreatment.T1, assignmentTreatment.treatmentName);
                    });
            })
            .catch((error) => {
                // Recorded before the metric so the enrolment is captured even if faultmetric is
                // undefined here, which it is when getAppConfig itself rejects.
                WeblabService.recordTreatment(featureId, WeblabService.PilotingValue.Unresolved);
                // Optional call: undefined in that same getAppConfig-rejected case, where an
                // unguarded call would throw a TypeError and mask the real error.
                faultmetric?.count(1);
                console.error('Error Detected when calling getTreatmentAssignment', error);
                return Promise.reject(error);
            });
    };

    private static recordTreatment = (featureId: string, treatment: string): void => {
        WeblabService.resolvedTreatments[featureId] = treatment;
    };
}
